// ============================================
// Route Guards - Protected Route Components
// ============================================

import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { UserRole } from '@conai/shared-types';
import { ADMIN_ROUTES, type PermissionKey, hasPermission, hasAnyPermission } from '@conai/shared-constants';
import { useAuthStore, selectIsAuthenticated, selectIsHydrated, selectUserRole } from '../stores';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Loading spinner component
 */
function LoadingSpinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
    </div>
  );
}

/**
 * Guard that requires authentication
 */
export function RequireAuth({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const isHydrated = useAuthStore(selectIsHydrated);
  const location = useLocation();

  // Show loading while hydrating from localStorage
  if (!isHydrated) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    // Redirect to login with return URL
    return <Navigate to={ADMIN_ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

interface RequireRoleProps extends ProtectedRouteProps {
  allowedRoles: UserRole[];
  fallbackPath?: string;
}

/**
 * Guard that requires specific roles
 */
export function RequireRole({ children, allowedRoles, fallbackPath = ADMIN_ROUTES.DASHBOARD }: RequireRoleProps) {
  const role = useAuthStore(selectUserRole);

  if (!role || !allowedRoles.includes(role)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}

interface RequirePermissionProps extends ProtectedRouteProps {
  permission: PermissionKey;
  fallbackPath?: string;
}

/**
 * Guard that requires a specific permission
 */
export function RequirePermission({ children, permission, fallbackPath = ADMIN_ROUTES.DASHBOARD }: RequirePermissionProps) {
  const role = useAuthStore(selectUserRole);

  if (!role || !hasPermission(role, permission)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}

interface RequireAnyPermissionProps extends ProtectedRouteProps {
  permissions: PermissionKey[];
  fallbackPath?: string;
}

/**
 * Guard that requires any of the specified permissions
 */
export function RequireAnyPermission({ children, permissions, fallbackPath = ADMIN_ROUTES.DASHBOARD }: RequireAnyPermissionProps) {
  const role = useAuthStore(selectUserRole);

  if (!role || !hasAnyPermission(role, permissions)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}

interface GuestOnlyProps {
  children: ReactNode;
  redirectTo?: string;
}

/**
 * Guard that only allows unauthenticated users (for login page)
 */
export function GuestOnly({ children, redirectTo = ADMIN_ROUTES.DASHBOARD }: GuestOnlyProps) {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const isHydrated = useAuthStore(selectIsHydrated);
  const location = useLocation();

  // Show loading while hydrating from localStorage
  if (!isHydrated) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    // Redirect to the page they came from or dashboard
    const from = (location.state as { from?: Location })?.from?.pathname || redirectTo;
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}
